import { Image } from "@nextui-org/react";
import { useState, MouseEvent, useEffect } from "react";
import { IProduct, IProductVariant } from "@/interfaces/product";
import { isArray, isEmpty, isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "@/hooks/useRedux";
import { notify } from "@/components/Shares/Notification";
import NumberSelector from "@/components/Shares/NumberSelector";
import { cart, fetchAddCart, openCart } from "@/store/slices/cartSlice";
import EStateAsyncStatus from "@/constants/stateAsyncState";
import SkeletonElement from "@/components/Shares/SkeletonElement";
import { Tooltip, Button, Checkbox } from "@nextui-org/react";
import { Rating, Star } from "@smastrom/react-rating";
import NoImage from "@/assets/no-image.jpg";
import namer from "color-namer";
import Collapse from "@/components/Shares/Collapse";
import { formatPrice } from "@/utils/format";

interface ProductInfoProps {
  product: IProduct;
  selectedVariant: IProductVariant;
  setSelectedVariant: (value: IProductVariant) => void;
}

const ProductInfo: React.FC<ProductInfoProps> = (props) => {
  const { product, selectedVariant, setSelectedVariant } = props;
  const {
    name,
    productVariants,
    features,
    shortDescription,
    longDescription,
    category,
    slug,
  } = product;
  const [productNum, setProductNum] = useState(
    selectedVariant.totalInventory === 0 ? 0 : 1,
  );
  const [isAssembly, setIsAssembly] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const cartState = useAppSelector(cart);

  const handleAddToCart = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (
      isEqual(cartState.status, EStateAsyncStatus.succeeded) &&
      cartState.cart
    ) {
      if (productNum <= 0) {
        notify("Failed to add to cart, please select quantity", "error");
        return;
      }
      const payload = {
        productVariantId: selectedVariant.id,
        quantity: productNum,
        shoppingCartId: cartState.cart.id,
        isAssembly,
      };
      await dispatch(fetchAddCart(payload));
      dispatch(openCart());
    }
  };

  const handleScoll = () => {
    window.dispatchEvent(new Event("scrollToReviews"));
  };

  useEffect(() => {
    if (selectedVariant) {
      setProductNum(selectedVariant.totalInventory === 0 ? 0 : 1);
    }
  }, [selectedVariant]);

  return (
    <div>
      {/* <div className="mb-6">
        {selectedVariant.totalInventory > 0 ? (
          selectedVariant.totalInventory > 10 ? (
            <span className="bg-icon text-gray-700 p-2 text-sm rounded-lg select-none hover:text-icon hover:bg-gray-950">
              {selectedVariant.totalInventory} in Stock
            </span>
          ) : (
            <span className="bg-red-600 text-gray-200 p-2 text-sm rounded-lg select-none hover:bg-icon">
              Only {selectedVariant.totalInventory} Left
            </span>
          )
        ) : (
          <span className="bg-gray-950 text-gray-200 p-2 text-sm rounded-lg select-none hover:bg-icon">
            OUT OF STOCK
          </span>
        )}
      </div> */}
      {selectedVariant.totalInventory <= 0 && (
        <div className="mb-6">
          <span className="bg-gray-950 text-gray-200 p-2 text-sm rounded-lg select-none hover:bg-icon">
            OUT OF STOCK
          </span>
        </div>
      )}
      <h1 className="text-3xl font-semibold">{selectedVariant.name}</h1>
      {!isEmpty(selectedVariant.reviews) && (
        <div className="flex items-center gap-2">
          <span>Reviews:</span>
          <Tooltip
            content={
              <div className="bg-white py-3 px-2">
                <div className="flex gap-2">
                  <Rating
                    readOnly
                    value={
                      selectedVariant.reviews.reduce(
                        (sum, review) => sum + review.rate,
                        0,
                      ) / selectedVariant.reviews.length
                    }
                    style={{ maxWidth: 80 }}
                    itemStyles={{
                      itemShapes: Star,
                      activeFillColor: "#B79B6C",
                      inactiveFillColor: "#FFEACF",
                    }}
                  />
                  <h2 className="font-bold text-lg">
                    {(
                      selectedVariant.reviews.reduce(
                        (sum, review) => sum + review.rate,
                        0,
                      ) / selectedVariant.reviews.length
                    ).toFixed(1)}{" "}
                    Out of 5
                  </h2>
                </div>
              </div>
            }
          >
            <span>
              <Rating
                readOnly
                value={
                  selectedVariant.reviews.reduce(
                    (sum, review) => sum + review.rate,
                    0,
                  ) / selectedVariant.reviews.length
                }
                style={{ maxWidth: 80, cursor: "pointer" }}
                itemStyles={{
                  itemShapes: Star,
                  activeFillColor: "#B79B6C",
                  inactiveFillColor: "#FFEACF",
                }}
              />
            </span>
          </Tooltip>
          <span onClick={handleScoll} className="cursor-pointer underline">
            {selectedVariant.reviews.length > 1
              ? `${selectedVariant.reviews.length} ratings`
              : `${selectedVariant.reviews.length} rating`}
          </span>
        </div>
      )}
      {/* <div className="flex items-center my-3">
          <div className="pr-4 border-r">
            <p className="text-sm">Send money securely</p>
            <Image src="/paypal.svg" alt="zip" width={60} height={45} />
          </div>
          <div className="px-4">
            <p className="text-sm">From $10/week</p>
            <Image src="/zip.svg" alt="zip" width={45} height={30} />
          </div>
        </div> */}
      <div className="flex text-3xl gap-2 my-6 items-end">
        <p
          className={`${
            selectedVariant.oldPrice !== 0 ? "text-red-500" : "text-icon"
          } font-semibold`}
        >
          <span>{formatPrice(selectedVariant.price)}</span>
        </p>
        {selectedVariant.oldPrice !== 0 && (
          <div className="flex gap-2 text-lg">
            <p className={"line-through text-gray-500 font-light"}>
              <span>{formatPrice(selectedVariant.oldPrice)}</span>
            </p>
            <div className="bg-red-500 text-white px-2 rounded-md">
              <p>
                -
                {Math.round(
                  ((selectedVariant.oldPrice - selectedVariant.price) * 100) /
                    selectedVariant.oldPrice,
                )}
                %
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 py-6 border-t mt-6">
        <div className="text-lg flex gap-2">
          <label>Color:</label>
          <p>{namer(`${selectedVariant.color}`).ntc[0].name}</p>
        </div>
        <div className="flex gap-2">
          {!isEmpty(selectedVariant) &&
            productVariants.map((variant) => {
              const { id, color } = variant;
              return (
                <div
                  key={id}
                  className={`size-10 rounded-full flex items-center justify-center border select-none cursor-pointer ${
                    isEqual(selectedVariant.id, id)
                      ? "border-icon border-[2.5px]"
                      : "border-white"
                  }`}
                >
                  <div
                    style={{ backgroundColor: `${color}` }}
                    className={`size-8 border rounded-full `}
                    onClick={() => setSelectedVariant(variant)}
                  />
                </div>
              );
            })}
        </div>
      </div>
      <div className="py-3 border-y flex flex-col">
        <div className="text-sm">
          {selectedVariant.totalInventory > 10 ? (
            <p>Available: 10+ units in stock in Australia</p>
          ) : (
            <p>
              Limited Stock: Only{" "}
              <span
                className={`${
                  selectedVariant.totalInventory <= 3 && "font-bold"
                }`}
              >
                {selectedVariant.totalInventory} left
              </span>{" "}
              — order soon!
            </p>
          )}
        </div>
        <div className="flex items-center justify-start xl:justify-between gap-2 w-full mb-4">
          <div className="w-24">
            <Image
              src={
                !isEmpty(selectedVariant.images)
                  ? selectedVariant.images[0]
                  : NoImage.src
              }
              alt={selectedVariant.name}
              width={100}
              height={100}
              className="flex-[1.5]"
            />
          </div>
          <NumberSelector
            value={productNum}
            setValue={setProductNum}
            maxNumber={selectedVariant.totalInventory}
          />
          <span className="mx-4 hidden md:block">{selectedVariant.name}</span>
          <span className="flex ml-7 xl:ml-0">
            <span>{formatPrice(selectedVariant.price)}</span>
          </span>
        </div>
        {selectedVariant.canAssembly && (
          <Checkbox
            isSelected={isAssembly}
            onValueChange={setIsAssembly}
            radius="none"
            color="warning"
          >
            Assembly is available only for delivery service. Do you want us to
            assemble the product upon delivery? [+
            {formatPrice(selectedVariant.assemblyFee)}]
          </Checkbox>
        )}
      </div>
      <Button
        className="bg-icon w-full text-lg text-white py-7 rounded-full mt-5 shadow-card transition-all duration-500"
        onClick={handleAddToCart}
      >
        Add to Cart •
        {`${formatPrice(
          selectedVariant.price +
            (isAssembly ? selectedVariant.assemblyFee : 0),
        )}`}
      </Button>
      <div className="mt-10 border-t ">
        <Collapse
          title="Product Overview"
          detail={
            <div>
              <span>{shortDescription}</span>
            </div>
          }
        />
        {!isEmpty(features) && (
          <Collapse
            title="Product Material & Care"
            detail={
              <div>
                {features.map((feature, index) => {
                  const { key, value } = feature;
                  return (
                    <li
                      key={index}
                      className="flex justify-between max-w-[560px]"
                    >
                      <label className="flex-1">{key}:</label>
                      <span className="flex-1">{value}</span>
                    </li>
                  );
                })}
              </div>
            }
          />
        )}
        <Collapse
          title="Product Dimensions"
          detail={
            <div>
              {isArray(selectedVariant.dimensionImages) && (
                <div>
                  {selectedVariant.dimensionImages.map((item, index) => {
                    return (
                      <div key={index}>
                        <Image
                          src={item}
                          alt="dimension"
                          radius="none"
                          className="max-w-96"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex justify-between max-w-[560px]">
                <label className="flex-1">Product Height</label>
                <span className="flex-1">
                  {selectedVariant.dimensionHeight}cm
                </span>
              </div>
              <div className="flex justify-between max-w-[560px]">
                <label className="flex-1">Product Width</label>
                <span className="flex-1">
                  {selectedVariant.dimensionWidth}cm
                </span>
              </div>
              <div className="flex justify-between max-w-[560px]">
                <label className="flex-1">Product Depth</label>
                <span className="flex-1">
                  {selectedVariant.dimensionLength}cm
                </span>
              </div>
              <div className="flex justify-between max-w-[560px]">
                <label className="flex-1">Product Weight</label>
                <span className="flex-1">
                  {selectedVariant.productWeight}kg
                </span>
              </div>
            </div>
          }
        />
        <Collapse
          title="Product Warranty"
          detail={
            <div className="flex flex-col w-full">
              <div className="flex w-full text-sm">
                <div className="border-t flex-1 p-2 bg-gray-100">
                  <span>Product Warranty</span>
                </div>
                <div className="border-t p-2 flex-1">
                  <span>Yes</span>
                </div>
              </div>
              <div className="flex w-full text-sm">
                <div className="border-t flex-1 p-2 bg-gray-100">
                  <span>Warranty Length</span>
                </div>
                <div className="border-t p-2 flex-1">
                  <span>12 Months</span>
                </div>
              </div>
              <div className="flex w-full text-sm">
                <div className="border-t flex-1 p-2 bg-gray-100">
                  <span>Full or Limited Warranty</span>
                </div>
                <div className="border-t p-2 flex-1">
                  <span>Limited</span>
                </div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export const ProductInfoSkeleton = () => (
  <div>
    <div className="flex h-5 py-8"></div>
    <div className="mb-6 h-7">
      <SkeletonElement />
    </div>
    <h1 className="h-8">
      <SkeletonElement />
    </h1>
    <div className="h-36 my-6">
      <SkeletonElement />
    </div>
    <div className="h-8">
      <SkeletonElement />
    </div>
    <div className="mt-8 mb-2 h-5">
      <SkeletonElement />
    </div>
    <div className="text-sm flex items-center gap-2 py-1">
      <div className="flex gap-1">
        {Array.from({ length: 2 }, (_, index) => (
          <div className="size-8" key={index}>
            <SkeletonElement />
          </div>
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-1 text-sm py-3 border-t">
      {Array.from({ length: 3 }, (_, index) => (
        <li key={index} className="flex h-4 justify-between gap-12">
          <span className="flex-1">
            <SkeletonElement />
          </span>
          <span className="flex-1">
            <SkeletonElement />
          </span>
        </li>
      ))}
    </div>
    <div className="flex items-center h-8  justify-start xl:justify-between gap-2 w-full">
      <SkeletonElement />
    </div>
    <div className="h-8 w-24 mt-5">
      <SkeletonElement />
    </div>
  </div>
);

export default ProductInfo;
